
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import vSelect from 'vue-select';
  import JobCard from '@/views/job/_shared/JobCard.vue';
  import debounce from 'lodash/debounce';
  import { Validations } from 'vuelidate-property-decorators';
  import { required, minValue, maxLength } from 'vuelidate/lib/validators';
  import {
    Client,
    IJobModel,
    IJobItemModel,
    IJobItemActivityModel,
    ILocationModel,
    IJobItemScheduleModel,
    JobItemByNumberSearchResultModel,
    JobItemActivityModel,
    EmployeeModel
  } from '@/services/api.service';
  import {
    JobItemType,
    Status,
    JobType,
    AreaType,
    EnumModel,
    StatusEnums,
    JobTypeEnums,
    JobItemTypeEnums,
    AreaTypeEnums,
    ActivityInvoiceType,
    ActvityInvoiceTypeEnums
  } from '@/common/enumerations';

  import toJobItemTypeFormatter from '@/filters/jobitemtype.formatter';
  import toStatusFormatter from '@/filters/status.formatter';
  import toISODateFormatter from '@/filters/toISOdate.formatter';
  import toDateFormatter from '@/filters/todate.formatter';
  import * as StringUtils from '@/utilities/string.utils';
  import { EventBus } from '@/event-bus';
  import { JobItemActivityCreatedMessage } from '@/services/eventbus.models';

  // Custom validators
  function validsitetime(value: any) {
    try {
      const val = parseFloat(value);
      return val > 0 && val < 1440;
    } catch (e) {
      return false;
    }
  }

  @Component({
    components: {
      vSelect,
      JobCard
    },
    filters: {
      toJobItemType: toJobItemTypeFormatter,
      toStatus: toStatusFormatter,
      toISODate: toISODateFormatter,
      toDate: toDateFormatter
    },
    props: {
      jobItem: {
        type: Object as () => IJobItemModel,
        required: false
      },
      jobGid: String,
      jobNumber: String,
      status: Number,
      jobHasAdminTask: Boolean
    }
  })
  export default class AddJobItemActivity extends Vue {
    private jobItemNumber: string = '';
    private jobItemStatus: number = 1;
    private jobItemName: string = '';
    private jobItemGid: string = '';

    private employeeGid: string = '';
    private employees: EmployeeModel[] = [];
    private employeesPlaceholder: EmployeeModel | null = null;

    private activityDate: Date = new Date();
    private enteredSiteTimeInHours: number = 0;
    private enteredSiteTimeInMinutes: number = 0;
    private enteredTravelTimeInHours: number = 0;
    private enteredTravelTimeInMinutes: number = 0;
    private notes: string = '';
    private isAdminActivity: boolean = false;
    private createInvoice: boolean = false;

    private availableInvoiceTypes: EnumModel[] = [];
    private selectedInvoiceType: number = 0;

    private jobHasAdminTask!: boolean;

    // Form state handling
    private formIsValid: boolean | null = null;

    @Validations()
    private validations = {
      activityDate: { required },
      enteredSiteTimeInHours: { minValue: minValue(0) },
      enteredSiteTimeInMinutes: { minValue: minValue(0) },
      enteredTravelTimeInHours: { minValue: minValue(0) },
      enteredTravelTimeInMinutes: { minValue: minValue(0) }
    };

    private get showAdminWarning(): boolean {
      return this.jobHasAdminTask && this.isAdminActivity;
    }

    protected mounted() {
      if (this.$props.jobItem) {
        this.jobItemGid = this.$props.jobItem.gid;
        this.jobItemName = this.$props.jobItem.name;
        this.jobItemStatus = this.$props.jobItem.status;
        this.jobItemNumber = this.$props.jobNumber + '.' + this.$props.jobItem.jobItemNumber;
        this.availableInvoiceTypes.push(...ActvityInvoiceTypeEnums);
        // this.selectedInvoiceType = this.availableInvoiceTypes[0];
      }

      // @ts-ignore
      this.$Progress.start();
      // set the initial date to yesterday
      this.activityDate.setDate(this.activityDate.getDate() - 1);
      this.activityDate.setHours(0);
      this.activityDate.setMinutes(0);
      this.activityDate.setSeconds(0);
      this.activityDate.setMilliseconds(0);
      console.log('Initial activity date:', this.activityDate);

      this.fetchBaseData();
    }

    private async fetchBaseData() {
      this.fetchEmployees();
      this.fetchTravelTime();
    }

    private fetchEmployees() {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

      const employees = clientService.getEmployees().then(data => {
        const sortedData = data.sort((n1, n2) => StringUtils.stringCompare(n1.firstName || '', n2.firstName || ''));
        const employeesPlaceholder = new EmployeeModel();
        employeesPlaceholder.init({ firstName: '(Select Employee...)', lastName: '', gid: '' });
        sortedData.unshift(employeesPlaceholder);

        this.employees = sortedData;
      });
    }
    private fetchTravelTime() {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      console.log('Fetching travel time for location:', this.$props.jobItem.location);
      const postcode = this.$props.jobItem.location.postcode;
      const suburb = this.$props.jobItem.location.suburb;
      clientService.lookupTravelTime(suburb, postcode).then(data => {
        if (data) {
          const tim = data.timeInMinutes || 0;
          this.enteredTravelTimeInHours = Math.floor(tim / 60);
          this.enteredTravelTimeInMinutes = tim % 60;
        }
      });
    }

    private createJobActivity() {
      // Do the work here
      // Run the validation
      // @ts-ignore
      this.$v.$touch();
      // @ts-ignore
      if (this.$v.$invalid) {
        this.formIsValid = false;
        console.log('Submit failed. Form is invalid.');
      } else {
        // do your submit logic here
        this.formIsValid = true;
        console.log('Submit started. Form valid.');

        // Need to force the vars to be numbers
        // The + char is a shorthand cast to number
        const siteTime: number = +(this.enteredSiteTimeInHours * 60) + +this.enteredSiteTimeInMinutes;
        let travelTime: number = +(this.enteredTravelTimeInHours * 60) + +this.enteredTravelTimeInMinutes;

        if (this.isAdminActivity) {
          travelTime = 0;
        }
        const newActivity: JobItemActivityModel = new JobItemActivityModel();
        newActivity.init({
          employeeGid: this.employeeGid,
          jobItemGid: this.$props.jobItemGid,
          activityDateTime: this.activityDate,
          siteTimeInMinutes: siteTime,
          travelTimeInMinutes: travelTime,
          notes: this.notes,
          isAdminActivity: this.isAdminActivity,
          invoiceType: this.selectedInvoiceType,
        });
        console.log(newActivity);
        const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
        // @ts-ignore
        this.$Progress.start();
        clientService
          .createJobItemActivity(this.$props.jobGid, this.$props.jobItem.gid, newActivity)
          .then((data: JobItemActivityModel) => {
            // Route to the edit job list screen?
            // @ts-ignore
            this.$Progress.finish();
            // @ts-ignore
            this.$awn.success('Job Activity for ' + this.jobItemNumber + ' created successfully');
            this.$emit('job-item-activity-created', { jobItemActivity: data, createInvoice: this.createInvoice } );
            EventBus.$emit(
              'job-item-activity-created',
              new JobItemActivityCreatedMessage(this.$props.jobItem.gid, data)
            );
            this.closeModal();
          });
      }
    }

    @Watch('activityDate')
    private onActivityDateChanged() {
      console.log('New activity date: ', this.activityDate);
    }
    private cancelEdit() {
      console.log('Cancelling edit...');
      // this.$emit('close');
      this.closeModal();
    }

    private closeModal() {
      this.$modal.hide('modal-jobitemactivity-create');
    }
    private getStatus(value: number) {
      return toStatusFormatter(value).toLowerCase();
    }
  }
